import React, { useEffect } from 'react'
import styled from 'styled-components'
import { useRevealProps } from '../../MwPage/components/Reveal'
import { videoRegion } from '../../MwPage/heroVideo'
import { Eyebrow, Hi, iceLine, paper, Section, SectionHead, Stack } from '../../MwPage/styled'
import { useFocus } from '../focus'
import { Column, H2, Wrap } from '../styled'

const PLAYER_SCRIPT = 'https://fast.wistia.net/player.js'

// the prototype's .vframe: a thin white frame round the player
const Frame = styled.div`
  padding: 10px;
  border-radius: 28px;
  background: ${paper};
  border: 1.5px solid ${iceLine};
  box-shadow: 0 14px 40px rgba(76, 150, 190, 0.14);
`

// Wistia's responsive embed: the padding holds the 16:9 box, the iframe fills it
const Responsive = styled.div`
  position: relative;
  padding: 56.25% 0 0 0;
  border-radius: 20px;
  overflow: hidden;
  background: #000000;

  iframe {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border: 0;
  }
`

// Wistia's player script, once per page, so the embed is the standard one and Wistia's own settings apply to it
const usePlayerScript = () => {
  useEffect(() => {
    if (document.querySelector(`script[src="${PLAYER_SCRIPT}"]`)) return
    const script = document.createElement('script')
    script.src = PLAYER_SCRIPT
    script.async = true
    document.body.appendChild(script)
  }, [])
}

const RevealEyebrow = ({ children }) => <Eyebrow {...useRevealProps()}>{children}</Eyebrow>

/**
 * "Watch first": Martin's message under the hero, as Wistia's standard embed of the page's own UK or US cut.
 * Playback, thumbnail, controls and calls to action are set in Wistia, not here, so the team can change them
 * without a deploy. The silent in-place teaser the MW hero runs is deliberately not used here for that reason.
 *
 * @param offer  the visitor's prices, for the region that picks the UK or US cut
 */
const VideoSection = ({ offer }) => {
  const { video } = useFocus()
  const wid = video.wids[videoRegion(offer.currency)].landscape
  usePlayerScript()

  return (
    <Section $ice style={{ padding: 'clamp(32px, 4vw, 48px) 24px' }}>
      <Wrap>
        <Stack $gap="clamp(24px, 3vw, 32px)">
          <SectionHead>
            {video.eyebrow && <RevealEyebrow>{video.eyebrow}</RevealEyebrow>}
            <H2 {...useRevealProps()}>
              {video.headline} <Hi>{video.headlineAccent}</Hi>
            </H2>
          </SectionHead>
          <Column $max={880} {...useRevealProps()}>
            <Frame>
              <Responsive>
                <iframe
                  key={wid}
                  src={`https://fast.wistia.net/embed/iframe/${wid}?web_component=true&seo=false`}
                  title={video.label}
                  allow="autoplay; fullscreen"
                  allowFullScreen
                  className="wistia_embed"
                  name="wistia_embed"
                />
              </Responsive>
            </Frame>
          </Column>
        </Stack>
      </Wrap>
    </Section>
  )
}

export default VideoSection
